export const TRIGGER_BUTTON_ID = 'nova-chat-trigger';

const SPARKLE_SVG = `<svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M10 0L12.2 7.8L20 10L12.2 12.2L10 20L7.8 12.2L0 10L7.8 7.8L10 0Z" fill="currentColor" opacity="0.8"/>
</svg>`;

/**
 * Copy key visual styles from a sibling link so the trigger button blends
 * into the host app's navigation.
 */
function mimicSiblingStyles(button: HTMLButtonElement, container: Element): void {
  const sibling = container.querySelector('a');
  if (!sibling) return;

  const cs = getComputedStyle(sibling);
  Object.assign(button.style, {
    display: cs.display === 'none' ? 'flex' : cs.display,
    alignItems: cs.alignItems,
    gap: cs.gap,
    padding: cs.padding,
    margin: cs.margin,
    fontSize: cs.fontSize,
    fontWeight: cs.fontWeight,
    fontFamily: cs.fontFamily,
    lineHeight: cs.lineHeight,
    letterSpacing: cs.letterSpacing,
    color: cs.color,
    borderRadius: cs.borderRadius,
    textDecoration: 'none',
    width: cs.width === 'auto' ? '' : cs.width,
    boxSizing: cs.boxSizing,
  });

  // Match hover background if sibling has one
  button.addEventListener('mouseenter', () => {
    button.style.backgroundColor = 'rgba(0,0,0,0.04)';
  });
  button.addEventListener('mouseleave', () => {
    button.style.backgroundColor = 'transparent';
  });
}

function createButton(label: string, onClick: () => void, container?: Element): HTMLButtonElement {
  const button = document.createElement('button');
  button.id = TRIGGER_BUTTON_ID;
  button.type = 'button';
  button.setAttribute('aria-label', label);
  // Base reset — mimicSiblingStyles will override when a sibling link exists
  Object.assign(button.style, {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    font: 'inherit',
    color: 'inherit',
    padding: '8px 12px',
    borderRadius: '6px',
    width: '100%',
    textAlign: 'left',
    boxSizing: 'border-box',
  });
  button.innerHTML = SPARKLE_SVG;
  const span = document.createElement('span');
  span.textContent = label;
  button.appendChild(span);
  button.addEventListener('click', onClick);

  if (container) {
    mimicSiblingStyles(button, container);
  }

  return button;
}

/**
 * Auto-detect a suitable navbar/header container when no explicit selector
 * is provided. Looks for common patterns: nav elements, header, sidebar navs.
 */
function autoDetectContainer(): Element | null {
  // Prefer elements with common navbar/header roles or tags
  const candidates: Array<{ el: Element; score: number }> = [];

  // Check <nav> elements
  for (const nav of document.querySelectorAll('nav')) {
    let score = 1;
    // Prefer top-level or sidebar navs that are visible
    if (nav.offsetHeight === 0 || nav.offsetWidth === 0) continue;
    const rect = nav.getBoundingClientRect();
    // Top nav (within top 80px of viewport)
    if (rect.top < 80) score += 3;
    // Sidebar nav (tall, narrow-ish, left side)
    if (rect.height > 200 && rect.left < 100) score += 2;
    // Has links — more likely to be a real navigation
    if (nav.querySelectorAll('a').length >= 2) score += 1;
    candidates.push({ el: nav, score });
  }

  // Check <header> elements
  for (const header of document.querySelectorAll('header')) {
    if (header.offsetHeight === 0) continue;
    const rect = header.getBoundingClientRect();
    let score = 1;
    if (rect.top < 80) score += 2;
    candidates.push({ el: header, score });
  }

  // Check [role="navigation"] elements
  for (const el of document.querySelectorAll('[role="navigation"]')) {
    if ((el as HTMLElement).offsetHeight === 0) continue;
    let score = 2;
    if (el.querySelectorAll('a').length >= 2) score += 1;
    candidates.push({ el, score });
  }

  if (candidates.length === 0) return null;
  candidates.sort((a, b) => b.score - a.score);
  return candidates[0].el;
}

/**
 * Inject a trigger button into a container element. If the container doesn't
 * exist yet (SPA hasn't rendered), uses MutationObserver to wait for it.
 *
 * When selector is null, auto-detects a suitable nav/header container.
 *
 * Returns a cleanup function that removes the button and disconnects the observer.
 */
export function injectTriggerButton(
  selector: string | null,
  label: string,
  onClick: () => void,
): () => void {

  let button: HTMLButtonElement | null = null;
  let observer: MutationObserver | null = null;

  function inject(container: Element) {
    if (container.querySelector(`#${TRIGGER_BUTTON_ID}`)) return;
    button = createButton(label, onClick, container);
    container.appendChild(button);
  }

  // Explicit selector provided — use it directly
  if (selector) {
    const existing = document.querySelector(selector);
    if (existing) {
      inject(existing);
    } else {
      observer = new MutationObserver(() => {
        const el = document.querySelector(selector);
        if (el) {
          observer?.disconnect();
          observer = null;
          inject(el);
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  } else {
    // No selector — auto-detect a suitable nav/header container.
    // Wait for DOM ready (SPAs render async), then try detection with retry.
    const tryAutoDetect = () => {
      const container = autoDetectContainer();
      if (container) {
        inject(container);
        return true;
      }
      return false;
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        if (!tryAutoDetect()) {
          // SPA may not have rendered yet — observe for changes
          observer = new MutationObserver(() => {
            if (tryAutoDetect()) {
              observer?.disconnect();
              observer = null;
            }
          });
          observer.observe(document.body, { childList: true, subtree: true });
        }
      });
    } else {
      if (!tryAutoDetect()) {
        observer = new MutationObserver(() => {
          if (tryAutoDetect()) {
            observer?.disconnect();
            observer = null;
          }
        });
        observer.observe(document.body, { childList: true, subtree: true });
      }
    }
  }

  return () => {
    observer?.disconnect();
    observer = null;
    if (button && button.parentNode) {
      button.removeEventListener('click', onClick);
      button.parentNode.removeChild(button);
      button = null;
    }
  };
}
